import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMountedState } from 'react-use';
import { get, pick } from 'lodash';

import { __t, ProductsBlockKind } from '@mssgme/shared';
import { cls, isMatchId, isMobile, keyHash } from '@mssgme/helpers';
import { useNavigationUtils } from '@mssgme/hooks';
import { LandingsPropTypes, Spinner } from '@mssgme/ui';
import { SearchIcon } from '@mssgme/icons';
import { ProductItem } from '../../../../products';
import { useBlockTheme, useProductsApi, useWebsiteData } from '../../../hooks';
import { MoreButton } from '../../../UI';
import { BlockBase } from '../BlockBase';
import { CollectionsBar } from './CollectionsBar';

import styles from './Products.scss';

export const Item = ({ product, baseProductUrl, fromCollection, interactive, ...rest }) => {
    const to = useMemo(
        () =>
            interactive
                ? {
                    pathname: `${baseProductUrl}/${product.slug?.toLowerCase()}`,
                    state: { fromCollection },
                }
                : undefined,
        [baseProductUrl, interactive, product]
    );

    return <ProductItem product={product} tag={interactive ? Link : 'div'} to={to} {...rest} />;
};

Item.propTypes = {
    product: PropTypes.object.isRequired,
    interactive: PropTypes.bool,
    fromCollection: PropTypes.string,
    baseProductUrl: PropTypes.string,
    style: PropTypes.object,
};

export default function Products({ block, interactive, isMobileMode, ...rest }) {
    const website = useWebsiteData();
    const isMounted = useMountedState();
    const location = useLocation();
    const { generateUrl } = useNavigationUtils();
    const { theme, style } = useBlockTheme(block);
    const collectionIds = get(block, 'collections.include', []);
    const activeCollections = website.collections.filter((col) => col.published && col.publishedProductsCount);
    const showCollections = collectionIds.length
        ? collectionIds.map((id) => activeCollections.find(isMatchId(id))).filter(Boolean)
        : activeCollections;
    const isTabbed = theme.filterMode !== 'buttons';
    const [activeCollectionId, setActiveCollectionId] = useState(showCollections[0]?._id || null);

    const options = pick(block, ['products', 'total', 'sort']);
    const query = useMemo(
        () => ({
            ...options,
            limit: isMobile() ? 6 : 12,
            collections: activeCollectionId && isTabbed ? { include: [activeCollectionId] } : block.collections,
        }),
        [website.collections, keyHash(options), activeCollectionId, isTabbed]
    );
    const { hasNext, isFetching, items, getProducts } = useProductsApi({
        workspaceId: website.workspace,
        websiteId: website._id,
        query,
    });
    const handleMore = useCallback(() => getProducts(), [getProducts]);
    const baseProductUrl = useMemo(() => generateUrl('p'), [location.pathname]);
    const defaultStyle = useMemo(() => ({ color: theme.color }), [theme]);
    const getFromCollection = (product) => Object.keys(product.cols || {}).find((id) => collectionIds.includes(id));

    useEffect(() => {
        const firstId = showCollections[0]?._id;

        if (firstId !== activeCollectionId && !showCollections.find(isMatchId(activeCollectionId))) {
            if (isMounted()) {
                setActiveCollectionId(firstId);
            }
        }
    }, [activeCollectionId, showCollections]);

    return (
        <BlockBase {...cls([styles.root, isMobileMode && styles.mobileMode], rest)}>
            {!!block.title && (
                <div className={styles.title} style={defaultStyle}>
                    {block.title}
                </div>
            )}

            {showCollections.length > 1 && (
                <CollectionsBar
                    theme={theme}
                    style={defaultStyle}
                    items={showCollections}
                    interactive={interactive}
                    activeCollectionId={activeCollectionId}
                    onSelect={setActiveCollectionId}
                />
            )}

            <ul className={styles.list}>
                {items.map((product) => (
                    <li key={product._id}>
                        <Item
                            product={product}
                            interactive={interactive}
                            baseProductUrl={baseProductUrl}
                            fromCollection={activeCollectionId || getFromCollection(product)}
                            themeStyles={theme}
                            style={defaultStyle}
                        />
                    </li>
                ))}

                {!(items.length || isFetching) && (
                    <li key="empty-list" className={styles.empty} style={defaultStyle}>
                        <SearchIcon className={styles.icon} size={48} />
                        <div className={styles.description}>{__t('no_products')}</div>
                    </li>
                )}
            </ul>

            {isFetching ? (
                <Spinner />
            ) : (
                hasNext && (
                    <div className={styles.moreBtnContainer}>
                        <MoreButton
                            className={styles.moreBtn}
                            style={{ ...style, color: theme.productsCategoryTextColor }}
                            onClick={handleMore}
                            disabled={isFetching}
                        />
                    </div>
                )
            )}
        </BlockBase>
    );
}

Products.propTypes = {
    ...BlockBase.propTypes,
    block: LandingsPropTypes.block.isRequired,
};

Products.defaultProps = BlockBase.defaultProps;

Products.kind = ProductsBlockKind;
